import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { 
  ShoppingCart,
  UserCog,
  Calculator,
  Briefcase,
  Package,
  GitBranch,
  Loader2,
  Building2,
  Users,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const departmentConfig = {
  'satin-alma': { icon: ShoppingCart, label: 'Satın Alma', color: 'text-blue-600' },
  'insan-kaynaklari': { icon: UserCog, label: 'İnsan Kaynakları', color: 'text-purple-600' },
  'muhasebe': { icon: Calculator, label: 'Muhasebe', color: 'text-green-600' },
  'idari-isler': { icon: Briefcase, label: 'İdari İşler', color: 'text-orange-600' },
  'depo': { icon: Package, label: 'Depo', color: 'text-cyan-600' },
  'is-akisi': { icon: GitBranch, label: 'İş Akışı', color: 'text-pink-600' },
};

const BoardDepartment = () => {
  const { branchId, department } = useParams();
  const { branches } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [hrRequests, setHrRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [rejectedRequests, setRejectedRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const deptConfig = departmentConfig[department] || departmentConfig['insan-kaynaklari'];
  const DeptIcon = deptConfig.icon;
  const branch = branches?.find(b => b.id === branchId);

  // Talep detayını görüntüle
  const handleViewDetail = (request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  // Okundu olarak işaretle
  const markAsRead = useCallback(async () => {
    if (department === 'insan-kaynaklari') {
      try {
        const token = localStorage.getItem('fortis_token');
        await fetch(`${API_URL}/api/notifications/mark-read?item_type=hr_request&mark_all=true&category=board_pending`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Okundu işaretleme hatası:', error);
      }
    }
  }, [department]);

  useEffect(() => {
    if (branchId) {
      fetchData();
      // İnsan Kaynakları departmanında okundu olarak işaretle
      markAsRead();
    }
  }, [branchId, department, markAsRead]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch statistics
      const statsRes = await fetch(`${API_URL}/api/board/branches/${branchId}/statistics`, { headers });
      if (statsRes.ok) {
        setStatistics(await statsRes.json());
      }

      // Fetch HR requests - use correct endpoint for pending_management
      if (department === 'insan-kaynaklari') {
        const reqRes = await fetch(`${API_URL}/api/board/hr-requests/pending`, { headers });
        if (reqRes.ok) {
          const allRequests = await reqRes.json();
          // Filter by branch if needed
          const branchRequests = allRequests.filter(r => r.requester_branch_id === branchId);
          setHrRequests(branchRequests);
        }
        
        // Fetch rejected requests
        const rejRes = await fetch(`${API_URL}/api/board/hr-requests/rejected`, { headers });
        if (rejRes.ok) {
          const allRejected = await rejRes.json();
          const branchRejected = allRejected.filter(r => r.requester_branch_id === branchId);
          setRejectedRequests(branchRejected);
        }
        
        // Fetch approved requests
        const appRes = await fetch(`${API_URL}/api/board/hr-requests/approved`, { headers });
        if (appRes.ok) {
          const allApproved = await appRes.json();
          const branchApproved = allApproved.filter(r => r.requester_branch_id === branchId);
          setApprovedRequests(branchApproved);
        }
      } else {
        const reqRes = await fetch(`${API_URL}/api/board/branches/${branchId}/hr-requests`, { headers });
        if (reqRes.ok) {
          setHrRequests(await reqRes.json());
        }
      }

      // Fetch employees
      const empRes = await fetch(`${API_URL}/api/board/branches/${branchId}/employees`, { headers });
      if (empRes.ok) {
        setEmployees(await empRes.json());
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setActionNotes('');
    setActionDialogOpen(true);
  };

  const submitAction = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const actionParam = actionType === 'approve' ? 'approve' : 'reject';
      const params = new URLSearchParams({
        action: actionParam,
        note: actionNotes
      });
      
      const response = await fetch(
        `${API_URL}/api/board/hr-requests/${selectedRequest.id}/action?${params}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success(actionType === 'approve' ? 'Talep onaylandı' : 'Talep reddedildi');
        setActionDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Bağlantı hatası');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_branch: { label: 'Şube Bekliyor', variant: 'secondary' },
      pending_hr: { label: 'İK Bekliyor', variant: 'secondary' },
      pending_management: { label: 'Yönetim Bekliyor', variant: 'outline' },
      branch_approved: { label: 'Şube Onayladı', variant: 'outline' },
      branch_rejected: { label: 'Şube Reddetti', variant: 'destructive' },
      approved: { label: 'Onaylandı', variant: 'default' },
      rejected_by_branch: { label: 'Şube Reddetti', variant: 'destructive' },
      rejected_by_hr: { label: 'İK Reddetti', variant: 'destructive' },
      rejected_by_management: { label: 'Yönetim Reddetti', variant: 'destructive' },
      board_approved: { label: 'Kurul Onayladı', variant: 'default' },
      board_rejected: { label: 'Kurul Reddetti', variant: 'destructive' },
      rejected: { label: 'Reddedildi', variant: 'destructive' },
    };
    const config = statusConfig[status] || { label: status, variant: 'secondary' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--brand-500))]" />
      </div>
    );
  }

  const pendingRequests = hrRequests.filter(r => ['pending_management'].includes(r.status));
  const processedRequests = hrRequests.filter(r => ['approved', 'rejected_by_management'].includes(r.status));

  return (
    <div className="space-y-6" data-testid="board-department">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-[hsl(var(--neutral-100))]`}>
            <Building2 className={`h-6 w-6 ${deptConfig.color}`} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">
              {branch?.name || 'Şube'}
            </h1>
            <p className="text-[hsl(var(--muted-foreground))] flex items-center gap-1">
              <DeptIcon className="h-4 w-4" />
              {deptConfig.label}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Çalışanlar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-employees">
                {statistics.employees?.total || 0}
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {statistics.employees?.active || 0} aktif
              </p>
            </CardContent>
          </Card>

          <Card className="border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Bekleyen Talepler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[hsl(var(--warning))]" data-testid="stat-pending">
                {statistics.hr_requests?.pending || 0}
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                + {statistics.hr_requests?.branch_approved || 0} şube onaylı
              </p>
            </CardContent>
          </Card>

          <Card className="border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Onaylanan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[hsl(var(--success))]" data-testid="stat-approved">
                {statistics.hr_requests?.approved || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[hsl(var(--muted-foreground))]">Bugün Devam</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="stat-attendance">
                {statistics.attendance_today?.present || 0} / {statistics.attendance_today?.total || 0}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Content Tabs */}
      <Tabs defaultValue="requests" className="space-y-4">
        <TabsList>
          <TabsTrigger value="requests" className="gap-2">
            <FileText className="h-4 w-4" />
            Bekleyen Talepler
            {pendingRequests.length > 0 && (
              <Badge variant="destructive" className="ml-1">{pendingRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Onayladığım Talepler
            {approvedRequests.length > 0 && (
              <Badge variant="outline" className="ml-1 text-green-600">{approvedRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="rejected" className="gap-2">
            <XCircle className="h-4 w-4" />
            Red Edilen Talepler
            {rejectedRequests.length > 0 && (
              <Badge variant="outline" className="ml-1 text-red-600">{rejectedRequests.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="employees" className="gap-2">
            <Users className="h-4 w-4" />
            Çalışanlar
          </TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-4">
          {/* Pending Requests */}
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-[hsl(var(--warning))]" />
                Bekleyen Talepler
              </CardTitle>
              <CardDescription>Onay bekleyen talepler</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingRequests.length === 0 ? (
                <p className="text-center text-[hsl(var(--muted-foreground))] py-8">
                  Bekleyen talep bulunmuyor
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Çalışan</TableHead>
                      <TableHead>Form</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingRequests.map((req) => (
                      <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                        <TableCell className="font-medium">{req.employee_name}</TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-[hsl(var(--muted-foreground))] mr-2">
                            #{req.form_number || 'N/A'}
                          </span>
                          {req.form_template_name || req.form_name}
                        </TableCell>
                        <TableCell>
                          {new Date(req.created_at).toLocaleDateString('tr-TR')}
                        </TableCell>
                        <TableCell>{getStatusBadge(req.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetail(req)}
                              className="gap-1"
                              data-testid={`view-btn-${req.id}`}
                            >
                              <Eye className="h-4 w-4" />
                              Görüntüle
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAction(req, 'approve')}
                              className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
                              data-testid={`approve-btn-${req.id}`}
                            >
                              <CheckCircle className="h-4 w-4" />
                              Onayla
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAction(req, 'reject')}
                              className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`reject-btn-${req.id}`}
                            >
                              <XCircle className="h-4 w-4" />
                              Reddet
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Processed Requests */}
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="text-lg">İşlenen Talepler</CardTitle>
              <CardDescription>Daha önce işlenen talepler</CardDescription>
            </CardHeader>
            <CardContent>
              {processedRequests.length === 0 ? (
                <p className="text-center text-[hsl(var(--muted-foreground))] py-8">
                  İşlenen talep bulunmuyor
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Çalışan</TableHead>
                      <TableHead>Form</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Durum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {processedRequests.slice(0, 10).map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">{req.employee_name}</TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-[hsl(var(--muted-foreground))] mr-2">
                            #{req.form_number || 'N/A'}
                          </span>
                          {req.form_template_name || req.form_name}
                        </TableCell>
                        <TableCell>
                          {new Date(req.created_at).toLocaleDateString('tr-TR')}
                        </TableCell>
                        <TableCell>{getStatusBadge(req.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onayladığım Talepler Tab */}
        <TabsContent value="approved" className="space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Onayladığım Talepler
              </CardTitle>
              <CardDescription>Yönetim kurulu tarafından onaylanan talepler</CardDescription>
            </CardHeader>
            <CardContent>
              {approvedRequests.length === 0 ? (
                <p className="text-center text-[hsl(var(--muted-foreground))] py-8">
                  Onaylanan talep bulunmuyor
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Çalışan</TableHead>
                      <TableHead>Form</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {approvedRequests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">{req.requester_name}</TableCell>
                        <TableCell>
                          <span className="font-mono text-xs text-[hsl(var(--muted-foreground))] mr-2">
                            #{req.form_number || 'N/A'}
                          </span>
                          {req.form_template_name || req.form_name}
                        </TableCell>
                        <TableCell>
                          {new Date(req.created_at).toLocaleDateString('tr-TR')}
                        </TableCell>
                        <TableCell>
                          <Badge variant="default" className="bg-green-500">Kabul Edildi</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetail(req)}
                            className="gap-1"
                          >
                            <Eye className="h-4 w-4" />
                            Görüntüle
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Red Edilen Talepler Tab */}
        <TabsContent value="rejected" className="space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-500" />
                Red Edilen Talepler
              </CardTitle>
              <CardDescription>Yönetim kurulu tarafından reddedilen talepler</CardDescription>
            </CardHeader>
            <CardContent>
              {rejectedRequests.length === 0 ? (
                <p className="text-center text-[hsl(var(--muted-foreground))] py-8">
                  Red edilen talep bulunmuyor
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Çalışan</TableHead>
                      <TableHead>Form</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Red Nedeni</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rejectedRequests.map((req) => {
                      // Son red notunu bul
                      const history = req.status_history || req.history || [];
                      const rejectionEntry = [...history].reverse().find(h => h.status === 'rejected_by_management');
                      const rejectionNote = rejectionEntry?.note || 'Belirtilmemiş';
                      
                      return (
                        <TableRow key={req.id}>
                          <TableCell className="font-medium">{req.requester_name}</TableCell>
                          <TableCell>
                            <span className="font-mono text-xs text-[hsl(var(--muted-foreground))] mr-2">
                              #{req.form_number || 'N/A'}
                            </span>
                            {req.form_template_name || req.form_name}
                          </TableCell>
                          <TableCell>
                            {new Date(req.created_at).toLocaleDateString('tr-TR')}
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            <p className="text-sm text-red-600 truncate" title={rejectionNote}>
                              {rejectionNote}
                            </p>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetail(req)}
                              className="gap-1"
                            >
                              <Eye className="h-4 w-4" />
                              Görüntüle
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employees">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="text-lg">Şube Çalışanları</CardTitle>
              <CardDescription>{employees.length} çalışan</CardDescription>
            </CardHeader>
            <CardContent>
              {employees.length === 0 ? (
                <p className="text-center text-[hsl(var(--muted-foreground))] py-8">
                  Çalışan bulunmuyor
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ad Soyad</TableHead>
                      <TableHead>Pozisyon</TableHead>
                      <TableHead>Telefon</TableHead>
                      <TableHead>Durum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((emp) => (
                      <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                        <TableCell className="font-medium">{emp.name}</TableCell>
                        <TableCell>{emp.position_name}</TableCell>
                        <TableCell>{emp.phone}</TableCell>
                        <TableCell>
                          <Badge variant={emp.employment_status === 'active' ? 'default' : 'secondary'}>
                            {emp.employment_status === 'active' ? 'Aktif' : 'Pasif'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Talep Detayı
            </DialogTitle>
            <DialogDescription>
              {selectedRequest?.form_number} - {selectedRequest?.form_template_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 py-4">
              {/* Genel Bilgiler */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Talep Eden</Label>
                  <p className="font-medium">{selectedRequest.requester_name}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Tarih</Label>
                  <p className="font-medium">{new Date(selectedRequest.created_at).toLocaleDateString('tr-TR')}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Form Numarası</Label>
                  <p className="font-mono">{selectedRequest.form_number}</p>
                </div>
              </div>

              {/* Form Verileri */}
              {selectedRequest.form_data && (
                <div className="border rounded-lg p-4 bg-muted/50">
                  <Label className="text-sm font-semibold mb-3 block">Form Bilgileri</Label>
                  <div className="space-y-2">
                    {(() => {
                      const formData = selectedRequest.form_data.form_data || selectedRequest.form_data;
                      const fieldLabels = {
                        'avansMiktari': 'Avans Miktarı',
                        'taksitSayisi': 'Taksit Sayısı',
                        'avansSebebi': 'Avans Sebebi',
                        'talepEdenAdi': 'Talep Eden',
                        'talepTarihi': 'Talep Tarihi'
                      };
                      return Object.entries(formData).map(([key, value]) => {
                        if (typeof value === 'object' || value === null || value === undefined) return null;
                        const displayLabel = fieldLabels[key] || key;
                        const displayValue = key === 'avansMiktari' ? `${value} TL` : 
                                            key === 'taksitSayisi' ? `${value} Taksit` : String(value);
                        return (
                          <div key={key} className="flex justify-between text-sm py-1 border-b last:border-0">
                            <span className="text-muted-foreground">{displayLabel}:</span>
                            <span className="font-medium">{displayValue}</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* İşlem Geçmişi */}
              {(selectedRequest.status_history || selectedRequest.history) && (
                <div className="border rounded-lg p-4">
                  <Label className="text-sm font-semibold mb-3 block">İşlem Geçmişi</Label>
                  <div className="space-y-2">
                    {(selectedRequest.status_history || selectedRequest.history).map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-3 text-sm p-2 bg-muted/30 rounded">
                        <div className="flex-1">
                          <p className="font-medium">{entry.by_name || entry.action_by_name}</p>
                          <p className="text-muted-foreground text-xs">
                            {new Date(entry.timestamp || entry.action_at).toLocaleString('tr-TR')}
                          </p>
                          {entry.note && <p className="mt-1 text-sm">{entry.note}</p>}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {entry.status === 'pending_branch' ? 'Oluşturuldu' :
                           entry.status === 'pending_hr' ? 'İK\'ya İletildi' :
                           entry.status === 'pending_management' ? 'Yönetime İletildi' :
                           entry.status === 'approved' ? 'Onaylandı' :
                           entry.status?.includes('rejected') ? 'Reddedildi' : entry.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Kapat
            </Button>
            <Button 
              onClick={() => { setDetailDialogOpen(false); handleAction(selectedRequest, 'approve'); }}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Onayla
            </Button>
            <Button 
              variant="destructive"
              onClick={() => { setDetailDialogOpen(false); handleAction(selectedRequest, 'reject'); }}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Talebi Onayla' : 'Talebi Reddet'}
            </DialogTitle>
            <DialogDescription>
              {selectedRequest?.employee_name} - {selectedRequest?.form_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Not (Opsiyonel)</Label>
              <Textarea
                placeholder="İşlem için not ekleyin..."
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                data-testid="action-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActionDialogOpen(false)}
              disabled={processing}
            >
              İptal
            </Button>
            <Button
              variant={actionType === 'approve' ? 'default' : 'destructive'}
              onClick={submitAction}
              disabled={processing}
              data-testid="confirm-action-btn"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {actionType === 'approve' ? 'Onayla' : 'Reddet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardDepartment;
